import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import {
    FilePond as FP,
    FilePondFile,
    create,
    registerPlugin,
    FilePondErrorDescription,
    ProcessServerConfigFunction,
    RevertServerConfigFunction,
    FetchServerConfigFunction
} from "filepond";
import { getFetchUrl, getImageDownloadUrl, STATUS_CODES } from "./Data";

type FilePondResponse = {
    imageUrl: string;
    thumbnailUrl: string;
};

export type FilePondCustomEvent = CustomEvent<{
    images: Set<string>;
    thumbnails: Set<string>;
}>;

export default class FilePond {
    input: HTMLInputElement;
    form: HTMLFormElement;
    pond: FP;
    _images: Set<string> = new Set();
    _thumbnails: Set<string> = new Set();

    constructor(input: HTMLInputElement) {
        this.input = input;
        this.form = input.form;

        this.initUploader().initListeners();
    }

    private initUploader() {
        registerPlugin(FilePondPluginImagePreview);
        registerPlugin(FilePondPluginFileValidateType);

        this.pond = create(this.input);
        this.pond.setOptions({
            server: {
                process: this.onProcess.bind(this),
                revert: this.onRevert.bind(this),
                fetch: this.onFetch.bind(this)
            },
            chunkUploads: true,
            chunkSize: 20000,
            allowReorder: false,
            allowMultiple: true,
            maxFiles: 10,
            acceptedFileTypes: ["image/jpeg"],
            labelIdle: '<span class="filepond--label-action icon upload"></span>',
            styleItemPanelAspectRatio: "1",
            imagePreviewMaxHeight: 145,
            credits: false,
            labelThousandsSeparator: window.ValidityMessages.filepond.labelThousandsSeparator,
            labelDecimalSeparator: window.ValidityMessages.filepond.labelDecimalSeparator,
            labelInvalidField: window.ValidityMessages.filepond.labelInvalidField,
            labelFileWaitingForSize: window.ValidityMessages.filepond.labelFileWaitingForSize,
            labelFileSizeNotAvailable: window.ValidityMessages.filepond.labelFileSizeNotAvailable,
            labelFileCountSingular: window.ValidityMessages.filepond.labelFileCountSingular,
            labelFileCountPlural: window.ValidityMessages.filepond.labelFileCountPlural,
            labelFileLoading: window.ValidityMessages.filepond.labelFileLoading,
            labelFileAdded: window.ValidityMessages.filepond.labelFileAdded,
            labelFileLoadError: window.ValidityMessages.filepond.labelFileLoadError,
            labelFileRemoved: window.ValidityMessages.filepond.labelFileRemoved,
            labelFileRemoveError: window.ValidityMessages.filepond.labelFileRemoveError,
            labelFileProcessing: window.ValidityMessages.filepond.labelFileProcessing,
            labelFileProcessingComplete: window.ValidityMessages.filepond.labelFileProcessingComplete,
            labelFileProcessingAborted: window.ValidityMessages.filepond.labelFileProcessingAborted,
            labelFileProcessingError: window.ValidityMessages.filepond.labelFileProcessingError,
            labelFileProcessingRevertError: window.ValidityMessages.filepond.labelFileProcessingRevertError,
            labelTapToCancel: window.ValidityMessages.filepond.labelTapToCancel,
            labelTapToRetry: window.ValidityMessages.filepond.labelTapToRetry,
            labelTapToUndo: window.ValidityMessages.filepond.labelTapToUndo,
            labelButtonRemoveItem: window.ValidityMessages.filepond.labelButtonRemoveItem,
            labelButtonAbortItemLoad: window.ValidityMessages.filepond.labelButtonAbortItemLoad,
            labelButtonRetryItemLoad: window.ValidityMessages.filepond.labelButtonRetryItemLoad,
            labelButtonAbortItemProcessing: window.ValidityMessages.filepond.labelButtonAbortItemProcessing,
            labelButtonUndoItemProcessing: window.ValidityMessages.filepond.labelButtonUndoItemProcessing,
            labelButtonRetryItemProcessing: window.ValidityMessages.filepond.labelButtonRetryItemProcessing,
            labelButtonProcessItem: window.ValidityMessages.filepond.labelButtonProcessItem
        });

        return this;
    }

    private initListeners() {
        this.pond.on("processfile", (error: FilePondErrorDescription, file: FilePondFile) => {
            if (!error) {
                this.handleImages(JSON.parse(file.serverId), "add");
            }
        });

        document.addEventListener("FilePond:reset", this.reset.bind(this));

        return this;
    }

    public handleImages(urls: FilePondResponse, method: "add" | "delete"): void {
        const imageUrl = urls.imageUrl;
        const thumbnailUrl = urls.thumbnailUrl;

        const event = `FilePond:${method}File`;

        const images = this.images;
        const thumbnails = this.thumbnails;

        images[method](imageUrl);
        thumbnails[method](thumbnailUrl);

        this.images = images;
        this.thumbnails = thumbnails;

        document.dispatchEvent(
            new CustomEvent(event, {
                detail: {
                    images,
                    thumbnails
                }
            })
        );
    }

    private onProcess: ProcessServerConfigFunction = (fieldName, file, metadata, load, error, progress, abort) => {
        const formData = new FormData();
        formData.append(fieldName, file, file.name);

        const request = new XMLHttpRequest();
        request.open("POST", getFetchUrl("image-upload"));

        request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
        };

        request.onload = function () {
            if (request.status >= STATUS_CODES.SUCCESS && request.status < STATUS_CODES.REDIRECT) {
                load(request.responseText);
            } else {
                error("Error while uploading");
            }
        };

        request.send(formData);

        return {
            abort: () => {
                request.abort();
                abort();
            }
        };
    };

    private onRevert: RevertServerConfigFunction = (uniqueFileId, load, error) => {
        this.handleImages(JSON.parse(uniqueFileId), "delete");
        document.dispatchEvent(new Event("FilePond:removefile"));
        error("Error while deleting file");
        load();
    };

    private onFetch: FetchServerConfigFunction = async (url, load) => {
        this.images.delete(url);
        const filename = url.substring(url.lastIndexOf("/") + 1);
        const proxy_url = getImageDownloadUrl() + filename;
        const response = await fetch(proxy_url);
        const blob = await response.blob();
        load(blob);
    };

    public reset() {
        this.pond.removeFiles();
        this.images.clear();
        this.thumbnails.clear();
    }

    get images(): Set<string> {
        return this._images;
    }

    set images(v: Set<string>) {
        this._images = v;
    }

    get thumbnails(): Set<string> {
        return this._thumbnails;
    }

    set thumbnails(v: Set<string>) {
        this._thumbnails = v;
    }
}
